// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.RepeaterNode.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { filter, isEmpty, concat, get, reduce, isNil, size } from 'lodash/fp';
import { FUNCTION } from 'logic/data/Izenda.ReportPartFieldConstants';
const FUNCTION_GROUP_NAME = FUNCTION.GROUP.NAME;

export default class RepeaterNode {
  level: number = 0;
  fields: Array<any> = [];
  smartTagInContent: Array<any>[];
  fieldSupportSubtotal: Array<any> = [];
  subTotalSmartTag: Array<any> = [];
  placeHolderPattern: string = '';
  placeHolderKey: string = '';
  pattern: string = '';

  childNodes: Array<RepeaterNode> = [];

  get fieldHasSubtotalConfig() {
    return filter(field => get('fieldInfo.hasSubTotal', field), this.fieldSupportSubtotal);
  }

  get fieldHasNoGroupingData() {
    return filter(
      field =>
        !(
          get('fieldInfo.haveFunction', field) && get('fieldInfo.functionInfo.name', field) !== FUNCTION.DAYS_OLD.NAME
        ) || get('fieldInfo.functionInfo.userDefined', field),
      this.fields
    );
  }

  get fieldGroupingData() {
    return filter(field => get('fieldInfo.haveFunction', field), this.fields);
  }

  get totalFieldGroupingData() {
    const fieldsInChild = reduce(
      (result, item) => {
        return concat(result, item.totalFieldGroupingData);
      },
      [],
      this.childNodes
    );

    return concat(this.fieldGroupingData, fieldsInChild);
  }

  get totalFieldHasNoGroupingData() {
    const fieldsInChild = reduce(
      (result, item) => {
        return concat(result, item.totalFieldHasNoGroupingData);
      },
      [],
      this.childNodes
    );

    return concat(this.fieldHasNoGroupingData, fieldsInChild);
  }

  get fieldIsRunning() {
    if (this.level === 0 || this.childNodes.length === 0) return [];
    return filter(field => get('fieldInfo.field.isRunningField', field), this.fields);
  }

  get validSubtotalSmartTag() {
    return filter(item => item.isValidSubTotalSmartTag, this.smartTagInContent);
  }

  get hasSupportUnsort() {
    return isEmpty(this.childNodes);
  }

  get totalFields() {
    const fieldsInChild = reduce(
      (result, item) => {
        return concat(result, item.totalFields);
      },
      [],
      this.childNodes
    );

    return concat(this.fields, fieldsInChild);
  }

  get hasGroupingData() {
    return size(this.totalFieldGroupingData) === size(this.totalFields);
  }

  get totalFieldRunningField() {
    const fieldsInChild = reduce(
      (result, item) => {
        return concat(result, item.totalFieldRunningField);
      },
      [],
      this.childNodes
    );

    return concat(this.fieldIsRunning, fieldsInChild);
  }

  get allFieldIsNotGroupingData() {
    return size(this.totalFieldHasNoGroupingData) === size(this.totalFields);
  }

  constructor(repeaterNode) {
    this.populateData(repeaterNode);
  }

  /**
   * populate data for repeater node
   * @param repeaterNode {Object}
   */
  populateData(repeaterNode: any): void {
    this.childNodes = repeaterNode.childNodes;
    this.level = repeaterNode.level;
    this.fields = repeaterNode.fields;
    this.smartTagInContent = repeaterNode.smartTagInContent;
    this.fieldSupportSubtotal = repeaterNode.fieldSupportSubtotal;
    this.subTotalSmartTag = repeaterNode.subTotalSmartTag;
    this.placeHolderPattern = repeaterNode.placeHolderPattern;
    this.placeHolderKey = repeaterNode.placeHolderKey;
    this.pattern = repeaterNode.pattern;
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.RepeaterNode.js